import { useCallback, useState } from 'react';
import { reorderTicket } from '../../api/endpoints';
import { toApiError } from '../../api/errors';
import type { Id } from '../../api/types';
import type { BacklogRow } from '../../lib/ticket-tree';
import {
  neighboursForDrop,
  orderAfterDrop,
  REFUSAL_MESSAGE,
  reorderableIds,
  type DropRefusal,
} from './rank-drop';

/**
 * Drag-to-reorder state for the sprint backlog.
 *
 * 🔴🔴 THE DRAG IS SCOPED TO ONE SECTION, AND THE SECTION KEY IS CARRIED THROUGH THE DRAG ITSELF.
 *   `backlog_rank` is one sparse key per PROJECT, so a rank computed between two tickets of another
 *   sprint is a valid key that means nothing in this list. A cross-section drop is therefore a change
 *   of SPRINT, not of order — a different write with different consequences (points move between
 *   sprint totals, and the target sprint may be closed). ⇒ It is REFUSED, with the alternative named.
 *   ⛔ Silently reinterpreting the gesture would give the user one drag that sometimes reorders and
 *     sometimes reassigns, with nothing on screen saying which they are about to get.
 *
 * ⚠️ OPTIMISTIC, BUT NOT CREDULOUS. The new order is shown immediately, and if the PATCH fails the
 *   list SNAPS BACK and says why. ⛔ The failure that is not allowed here is the silent one: leaving
 *   the row where the user dropped it after the server refused would show an order the database does
 *   not have, and the next refresh would undo it with no explanation.
 *
 * 🔴 THE 409 IS EXPECTED, NOT EXCEPTIONAL. `PATCH /tickets/:id/rank` refuses a neighbour whose
 *   `backlog_rank` is NULL ("rank it first"), and `sort=rank` puts unranked tickets LAST — so the
 *   bottom of a backlog nobody has ordered by hand is exactly where they are. The message is passed
 *   through rather than replaced with "could not reorder".
 */
export interface ReorderState {
  /** The id being dragged, or null. */
  draggingId: Id | null;
  /** The section that drag started in — a drop anywhere else is refused. */
  draggingSection: string | null;
  /** The row a drop would land on. */
  overId: Id | null;
  busyId: Id | null;
  error: string | null;
  dismissError: () => void;
  /**
   * The in-flight display order for a section, or `null` when there is none.
   *
   * ⭐ `null` rather than "the current order" so the caller can skip the reshuffle entirely in the
   *   ordinary case — the server's order is already the right one, and re-deriving it would move
   *   nested children around in the input for no reason.
   */
  orderFor: (sectionKey: string, rows: readonly BacklogRow[]) => Id[] | null;
  begin: (sectionKey: string, id: Id) => void;
  end: () => void;
  over: (sectionKey: string, id: Id) => void;
  leave: (id: Id) => void;
  drop: (sectionKey: string, targetId: Id, rows: readonly BacklogRow[]) => void;
  move: (sectionKey: string, id: Id, delta: -1 | 1, rows: readonly BacklogRow[]) => void;
}

export function useRankReorder(onReordered: () => void): ReorderState {
  const [draggingId, setDraggingId] = useState<Id | null>(null);
  const [draggingSection, setDraggingSection] = useState<string | null>(null);
  const [overId, setOverId] = useState<Id | null>(null);
  const [busyId, setBusyId] = useState<Id | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The order shown while a write is in flight, per section. ⭐ Keyed by section so a pending move in
  // one does not reorder another.
  const [pending, setPending] = useState<Record<string, Id[]>>({});

  const orderFor = useCallback(
    (sectionKey: string, rows: readonly BacklogRow[]): Id[] | null => {
      const optimistic = pending[sectionKey];
      if (!optimistic) return null;

      // ⚠️ The optimistic order is only used while it still describes the SAME SET. If a refresh has
      //    added or removed a row, the stale arrangement is dropped rather than applied to a list it
      //    no longer matches — which would silently hide or duplicate a row.
      const actual = reorderableIds(rows);
      if (optimistic.length !== actual.length) return null;
      const live = new Set(actual);
      return optimistic.every((id) => live.has(id)) ? optimistic : null;
    },
    [pending],
  );

  const clearPending = useCallback((sectionKey: string) => {
    setPending((current) => {
      if (!(sectionKey in current)) return current;
      const next = { ...current };
      delete next[sectionKey];
      return next;
    });
  }, []);

  const refuse = useCallback((reason: Exclude<DropRefusal, 'unchanged'>) => {
    setError(REFUSAL_MESSAGE[reason]);
  }, []);

  const commit = useCallback(
    async (sectionKey: string, id: Id, targetId: Id, rows: readonly BacklogRow[]) => {
      const order = reorderableIds(rows);
      const result = neighboursForDrop(order, id, targetId);

      // Not an error and not a request — a click that moved a little.
      if (result === 'unchanged') return;
      if (typeof result === 'string') {
        refuse(result);
        return;
      }

      setError(null);
      setBusyId(id);
      setPending((current) => ({ ...current, [sectionKey]: orderAfterDrop(order, id, targetId) }));

      try {
        await reorderTicket(id, result);
        // ⭐ Refetch rather than keeping the optimistic array: the server assigns the actual
        //   `backlog_rank`, and this list is what the NEXT reorder computes neighbours from.
        onReordered();
        clearPending(sectionKey);
      } catch (caught) {
        // ⛔ Snap back. An order the database does not have must not survive on screen.
        clearPending(sectionKey);
        // ⭐ `reason` FIRST — it is the API's own words, and for the 409 that is the actionable half
        //   ("the following ticket has no backlog rank yet; rank it first"). The generic message is
        //   the fallback, not the headline.
        const apiError = toApiError(caught, `/tickets/${id}/rank`);
        setError(apiError.reason ?? apiError.message);
      } finally {
        setBusyId(null);
      }
    },
    [clearPending, onReordered, refuse],
  );

  const begin = useCallback((sectionKey: string, id: Id) => {
    setDraggingId(id);
    setDraggingSection(sectionKey);
    setError(null);
  }, []);

  const end = useCallback(() => {
    setDraggingId(null);
    setDraggingSection(null);
    setOverId(null);
  }, []);

  const over = useCallback(
    (sectionKey: string, id: Id) => {
      // ⚠️ No drop indicator on a row that cannot accept the drop — the highlight must not promise a
      //    move that will be refused.
      if (!draggingId || draggingSection !== sectionKey || draggingId === id) return;
      setOverId(id);
    },
    [draggingId, draggingSection],
  );

  const leave = useCallback((id: Id) => {
    setOverId((current) => (current === id ? null : current));
  }, []);

  const drop = useCallback(
    (sectionKey: string, targetId: Id, rows: readonly BacklogRow[]) => {
      const id = draggingId;
      const from = draggingSection;
      end();
      if (!id) return;
      if (from !== sectionKey) {
        refuse('different-section');
        return;
      }
      void commit(sectionKey, id, targetId, rows);
    },
    [commit, draggingId, draggingSection, end, refuse],
  );

  const move = useCallback(
    (sectionKey: string, id: Id, delta: -1 | 1, rows: readonly BacklogRow[]) => {
      const order = reorderableIds(rows);
      const from = order.indexOf(id);
      const to = from + delta;
      // ⭐ The buttons are disabled at the ends, so this is belt and braces rather than the guard —
      //   but a disabled attribute is a UI fact and this function is reachable without it.
      if (from < 0 || to < 0 || to >= order.length) return;
      void commit(sectionKey, id, order[to] as Id, rows);
    },
    [commit],
  );

  return {
    draggingId,
    draggingSection,
    overId,
    busyId,
    error,
    dismissError: () => setError(null),
    orderFor,
    begin,
    end,
    over,
    leave,
    drop,
    move,
  };
}
